import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  toggleStarSpace,
  fetchRecentlyViewedSpaces,
} from '@core/spaceSelector/operations';
import {
  accessingSpaceAsStaff,
  isInstructorInSpace,
} from '@core/selectors/classroom';
import { currentUser } from '@core/selectors/user';
import {
  getHomeStarredSpaces,
  getHomeSpacesForView,
} from '@app/selectors/home';
import { getHomeRecentSpaces } from '../components/presentational/Home/widgets/recentlyViewedSpacesWidget/getHomeRecentSpaces';
import { Space } from '@core/types/state';

const PAGE_SIZE = 20;

export const useAllSpaces = (
  filterType: 'all' | 'starred' | 'recent' = 'all'
) => {
  const dispatch = useDispatch<any>();

  const homeStarredSpaces = useSelector(getHomeStarredSpaces);
  const homeSpacesForView = useSelector(getHomeSpacesForView);
  const homeRecentSpaces = useSelector(getHomeRecentSpaces);

  // Convert map to sorted array like the dropdown does, and filter out archived spaces
  const realSpaces = useMemo(() => {
    return homeSpacesForView || [];
  }, [homeSpacesForView]);

  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Fetch recently viewed spaces if not already fetched
  useEffect(() => {
    if (filterType === 'recent') {
      dispatch(fetchRecentlyViewedSpaces() as any);
    }
  }, [dispatch, filterType]);

  // We simulate a small delay for loading more to show the skeleton smoothly if needed
  // In a real API pagination, this would be an actual fetch request.
  const onLoadMore = useCallback(() => {
    if (isLoadingMore) return;
    setIsLoadingMore(true);

    // Simulate network request for progressive rendering
    setTimeout(() => {
      setPage((p) => p + 1);
      setIsLoadingMore(false);
    }, 400);
  }, [isLoadingMore]);

  const user = useSelector(currentUser);
  const isStaff = user?.userRoles?.includes('staff') || false;

  // Generate base list of spaces based on filterType
  const baseSpaces = useMemo(() => {
    let initialSpaces: Space[] = [];
    if (filterType === 'starred') {
      initialSpaces = homeStarredSpaces || [];
    } else if (filterType === 'recent') {
      initialSpaces = homeRecentSpaces || [];
    } else {
      initialSpaces = realSpaces || [];
    }

    return initialSpaces;
  }, [realSpaces, filterType, homeStarredSpaces, homeRecentSpaces]);

  const state = useSelector((state: any) => state);

  const filteredBaseSpaces = useMemo(() => {
    return baseSpaces.filter((s: any) => {
      // Allow all spaces (including staff access) in Recently Viewed lists
      if (filterType === 'recent') {
        return true;
      }

      const spaceId = s.objectId || s.id;
      let isStaffAccess = s.isStaffAccess;
      if (isStaffAccess === undefined) {
        isStaffAccess = accessingSpaceAsStaff(state)(spaceId);
      }

      // For All Spaces and Starred lists, filter out Staff Access spaces and explicitly unassigned spaces
      return !isStaffAccess && s.isAssigned !== false;
    });
  }, [baseSpaces, filterType, state, accessingSpaceAsStaff]);

  const spaces = useMemo(() => {
    return filteredBaseSpaces.slice(0, page * PAGE_SIZE).map((s: any) => {
      const spaceId = s.objectId || s.id;

      let isStaffAccess = s.isStaffAccess;
      if (isStaffAccess === undefined) {
        isStaffAccess = accessingSpaceAsStaff(state)(spaceId);
      }

      return {
        id: spaceId,
        name: s.name,
        startDate: s.startDate,
        endDate: s.endDate,
        lastNavigation: s.lastNavigation,
        avatar: s.avatar || null,
        isStaffAccess,
        isAssigned: s.isAssigned,
        archived: s.archived || false,
      };
    });
  }, [
    filteredBaseSpaces,
    page,
    state,
    accessingSpaceAsStaff,
    isInstructorInSpace,
  ]);

  const hasMore = filteredBaseSpaces.length > page * PAGE_SIZE;

  const starredIds = useMemo(() => {
    const set = new Set<string>();
    (realSpaces || []).forEach((s: any) => {
      if (s.isStarred) {
        set.add(s.objectId || s.id);
      }
    });
    return set;
  }, [realSpaces]);

  const handleStarToggle = useCallback(
    (spaceId: string) => {
      dispatch(toggleStarSpace({ spaceId }));
    },
    [dispatch]
  );

  // Derive initial loading state from global data fetch to prevent premature empty-state evaluation on hard reloads
  const isLoading = useSelector((state: any) => !state.data?.hasInitialData);

  return {
    spaces,
    isLoading,
    isLoadingMore,
    hasMore,
    onLoadMore,
    starredIds,
    handleStarToggle,
    isStaff,
  };
};

export default useAllSpaces;
